/**
 * Request deduplication and caching utility
 * Prevents duplicate API calls and caches responses
 */

interface CacheEntry<T = unknown> {
  promise: Promise<T>
  timestamp: number
  data?: T
  error?: Error
}

class RequestCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map()
  private readonly TTL = 5000 // Cache for 5 seconds to handle StrictMode and quick navigation
  private readonly STORAGE_KEY = 'joulesadmin_request_cache'
  private readonly USE_STORAGE = true // Enable sessionStorage for persistence

  constructor() {
    // Load cache from storage on initialization
    if (this.USE_STORAGE) {
      this.loadFromStorage()
    }
  }

  /**
   * Load cache from sessionStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        const now = Date.now()
        // Restore non-expired entries
        Object.entries(parsed).forEach(([key, entry]) => {
          const cacheEntry = entry as { timestamp?: number; data?: unknown }
          if (cacheEntry.timestamp && now - cacheEntry.timestamp < this.TTL) {
            this.cache.set(key, {
              ...cacheEntry,
              timestamp: cacheEntry.timestamp,
              promise: Promise.resolve(cacheEntry.data) // Restore as resolved promise
            })
          }
        })
      }
    } catch (error) {
      console.error('Error loading request cache from storage:', error)
    }
  }

  /**
   * Save cache to sessionStorage (only successful responses)
   */
  private saveToStorage(): void {
    if (!this.USE_STORAGE) return
    
    try {
      const toStore: Record<string, unknown> = {}
      this.cache.forEach((entry, key) => {
        // Only store successful responses with data
        if (entry.data && !entry.error) {
          toStore[key] = {
            data: entry.data,
            timestamp: entry.timestamp
          }
        }
      })
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(toStore))
    } catch (error) {
      console.error('Error saving request cache to storage:', error)
    }
  }

  /**
   * Generate a cache key from request parameters
   */
  private getCacheKey(url: string, params?: unknown): string {
    // For URLs with query params, use the full URL as the key
    // This ensures different search queries get different cache entries
    if (url.includes('?')) {
      return url
    }
    // For other cases, append params
    const paramString = params ? JSON.stringify(params) : ''
    return `${url}::${paramString}`
  }

  /**
   * Clear expired cache entries
   */
  private clearExpired(): void {
    const now = Date.now()
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.TTL) {
        this.cache.delete(key)
      }
    }
  }

  /**
   * Get or create a cached request
   * This prevents duplicate requests for the same endpoint and parameters
   */
  async getOrFetch<T>(
    url: string,
    fetcher: () => Promise<T>,
    params?: unknown
  ): Promise<T> {
    this.clearExpired()
    
    const key = this.getCacheKey(url, params)
    const existing = this.cache.get(key)
    
    // If we have a recent cached response, return it
    if (existing && existing.data !== undefined) {
      const age = Date.now() - existing.timestamp
      if (age < this.TTL) {
        console.log(`[Cache] Returning cached data for ${url} (age: ${age}ms)`)
        return existing.data as T
      }
    }
    
    // If there's an in-flight request, wait for it
    if (existing && !existing.data && !existing.error) {
      console.log(`[Cache] Waiting for in-flight request to ${url}`)
      try {
        const result = await existing.promise
        return result as T
      } catch (error) {
        // If the in-flight request failed, remove it and retry
        this.cache.delete(key)
        throw error
      }
    }
    
    // Create new request
    console.log(`[Cache] Making new request to ${url}`)
    const promise = fetcher()
    
    // Store the promise immediately to prevent duplicate requests
    const entry: CacheEntry<T> = {
      promise,
      timestamp: Date.now()
    }
    this.cache.set(key, entry)
    
    try {
      const result = await promise
      // Update cache with successful result
      entry.data = result
      // Save to storage after successful fetch
      this.saveToStorage()
      return result
    } catch (error) {
      // Update cache with error
      entry.error = error as Error
      this.cache.delete(key) // Remove failed requests from cache
      throw error
    }
  }

  /**
   * Clear the entire cache
   */
  clear(): void {
    this.cache.clear()
    if (this.USE_STORAGE) {
      try {
        sessionStorage.removeItem(this.STORAGE_KEY)
      } catch (error) {
        console.error('Error clearing request cache from storage:', error)
      }
    }
  }

  /**
   * Clear a specific cache entry
   */
  clearKey(url: string, params?: unknown): void {
    const key = this.getCacheKey(url, params)
    this.cache.delete(key)
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    this.clearExpired()
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    }
  }
}

// Export singleton instance
export const requestCache = new RequestCache()